import { assertActiveSessionRequest, assertIframeMessage, type GameSession, type PlatformAdapter, type WalletSnapshot } from "@wild-blooms/platform-contracts";

export type BridgeStatus = "waiting" | "connected" | "rejected";
export interface IframeBridge { readonly nonce: string; status(): BridgeStatus; attach(frame: HTMLIFrameElement): () => void; bootstrap(): void; }

function nonce(): string {
  const values = crypto.getRandomValues(new Uint32Array(8));
  return [...values].map(value => value.toString(16).padStart(8, "0")).join("");
}

/** Strictly validates the sandboxed game's source window, startup nonce and allowed request shapes. */
export function createIframeBridge(adapter: PlatformAdapter, onStatus: (status: BridgeStatus) => void, getActiveSession: () => GameSession | null, onWallet: (wallet: WalletSnapshot) => void): IframeBridge {
  const startupNonce = nonce(); let frame: HTMLIFrameElement | null = null; let state: BridgeStatus = "waiting";
  let retryTimer: ReturnType<typeof setTimeout> | null = null; let retryAttempts = 0;
  const clearBootstrapRetry = () => { if (retryTimer !== null) clearTimeout(retryTimer); retryTimer = null; };
  const setState = (next: BridgeStatus) => { state = next; onStatus(next); };
  const handler = async (event: MessageEvent<unknown>) => {
    if (!frame?.contentWindow || event.source !== frame.contentWindow) return;
    try {
      // The child sends this keyless signal only after installing its listener.
      // It carries no nonce, session, result, or financial assertion.
      if (typeof event.data === "object" && event.data !== null && Object.keys(event.data).length === 1 && (event.data as { type?: unknown }).type === "wb:hello") { bootstrap(); return; }
      assertIframeMessage(event.data);
      if (event.data.nonce !== startupNonce) throw new Error("iframe nonce mismatch");
      const activeSession = getActiveSession();
      if (event.data.type === "wb:ready") { if (!activeSession || event.data.sessionId !== activeSession.id) throw new Error("no active game session"); clearBootstrapRetry(); setState("connected"); return; }
      if (event.data.type !== "wb:request-spin" && event.data.type !== "wb:request-double-start" && event.data.type !== "wb:request-double-action" && event.data.type !== "wb:request-double-collect") return;
      if (state !== "connected") throw new Error("iframe handshake is not connected");
      assertActiveSessionRequest(event.data.request, activeSession?.id ?? null);
      const requestId = event.data.request.idempotencyKey;
      let payload;
      try {
        payload = event.data.type === "wb:request-spin" ? await adapter.requestSpin(event.data.request)
          : event.data.type === "wb:request-double-start" ? await adapter.requestDoubleStart(event.data.request)
          : event.data.type === "wb:request-double-action" ? await adapter.requestDoubleAction(event.data.request)
          : await adapter.requestDoubleCollect(event.data.request);
      } catch (requestError) {
        // A dropped response can still have committed exactly once. Resolve by the same key;
        // never let the iframe infer a balance or retry with a new key.
        const resolved = await adapter.resolveTransaction(requestId);
        if (resolved.status !== "settled" || !resolved.response) throw requestError;
        payload = resolved.response;
      }
      onWallet(payload.wallet);
      frame.contentWindow.postMessage({ type: "wb:response", nonce: startupNonce, requestId, ok: true, payload }, "*");
    } catch (error) {
      setState("rejected");
      const requestId = typeof event.data === "object" && event.data && "request" in event.data && typeof (event.data as { request?: { idempotencyKey?: unknown } }).request?.idempotencyKey === "string" ? (event.data as { request: { idempotencyKey: string } }).request.idempotencyKey : "unknown";
      frame.contentWindow?.postMessage({ type: "wb:response", nonce: startupNonce, requestId, ok: false, error: error instanceof Error ? error.message : "invalid game request" }, "*");
    }
  };
  const sendBootstrap = () => {
    const session = getActiveSession();
    if (!frame?.contentWindow || !session) return;
    frame.contentWindow.postMessage({ type: "wb:bootstrap", nonce: startupNonce, apiVersion: "v1", sessionId: session.id, expectedRevision: session.revision }, "*");
  };
  /** Replays the one-time child handshake across attach/load/session ordering races. */
  const bootstrap = () => {
    clearBootstrapRetry(); retryAttempts = 0;
    const attempt = () => {
      if (!frame || state === "connected") { clearBootstrapRetry(); return; }
      // No message is sent until an authoritative parent session exists.
      retryAttempts++;
      if (getActiveSession()) sendBootstrap();
      if (retryAttempts >= 20) { clearBootstrapRetry(); if (getActiveSession()) setState("rejected"); return; }
      retryTimer = setTimeout(attempt, 250);
    };
    attempt();
  };
  return {
    nonce: startupNonce, status: () => state, bootstrap,
    attach(nextFrame) {
      frame = nextFrame; window.addEventListener("message", handler);
      const load = () => { setState("waiting"); bootstrap(); };
      nextFrame.addEventListener("load", load);
      // The iframe may already have loaded before this React effect attaches.
      bootstrap();
      return () => { clearBootstrapRetry(); window.removeEventListener("message", handler); nextFrame.removeEventListener("load", load); frame = null; };
    },
  };
}
